import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ClipboardCheck, Loader2, Plus, Search, Trash2, X, UserRound, Star } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { EmptyState, ListSkeleton } from "@/components/studio/StudioUI";
import { studio, apiError, beDate, inputCls, btnPrimary, TRADE_OPTIONS, JOB_TYPES } from "@/lib/studioApi";

const FILTERS = [["tous", "Toutes"], ["en_cours", "En cours"], ["envoye", "Envoyées"], ["signe", "Signées"]];
const ivState = (iv) => (iv.signature ? "signe" : iv.sent_at ? "envoye" : "en_cours");
const STATE_META = {
  en_cours: ["En cours", "bg-amber-50 text-amber-700 border-amber-200", "bg-amber-100 text-amber-600"],
  envoye: ["Envoyée", "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]", "bg-[#EEF1FC] text-[#3A5CC8]"],
  signe: ["Signée", "bg-emerald-50 text-emerald-700 border-emerald-200", "bg-emerald-100 text-emerald-600"],
};

const empty = {
  client_name: "", client_email: "", client_phone: "",
  site_address: "", site_postal_code: "", site_city: "",
  trade: "chauffagiste", job_type: "depannage",
};

export default function StudioInterventions() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(params.get("nouveau") === "1");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState(params.get("filtre") || "tous");
  const [search, setSearch] = useState("");

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items || []).filter((iv) => {
      if (filter !== "tous" && ivState(iv) !== filter) return false;
      if (!q) return true;
      return [iv.reference, iv.client_name, iv.site_city, iv.dossier_reference].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [items, search, filter]);
  const counts = useMemo(() => (items || []).reduce((acc, iv) => {
    ["tous", ivState(iv)].forEach((k) => { acc[k] = (acc[k] || 0) + 1; });
    return acc;
  }, {}), [items]);

  useEffect(() => {
    studio.get("/interventions").then(({ data }) => setItems(data)).catch(() => toast.error("Chargement impossible."));
  }, []);

  const jobTypes = JOB_TYPES[form.trade] || JOB_TYPES.autre;

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await studio.post("/interventions", form);
      toast.success(`Intervention ${data.reference} créée.`);
      navigate(`/studio/interventions/${data.id}`);
    } catch (err) {
      toast.error(apiError(err, "Création impossible."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (iv) => {
    if (!window.confirm(`Supprimer l'intervention ${iv.reference} ?`)) return;
    try {
      await studio.delete(`/interventions/${iv.id}`);
      setItems((list) => (list || []).filter((x) => x.id !== iv.id));
      toast.success("Intervention supprimée.");
    } catch (err) {
      toast.error(apiError(err, "Suppression impossible."));
    }
  };

  return (
    <StudioLayout
      title="Interventions"
      subtitle="Vous intervenez d'abord, vous ouvrez le dossier ensuite si un devis est nécessaire."
      action={
        <button type="button" data-testid="new-intervention-btn" onClick={() => setOpen(!open)} className={btnPrimary}>
          {open ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {open ? "Fermer" : "Nouvelle intervention"}
        </button>
      }
    >
      {open && (
        <form data-testid="new-intervention-form" onSubmit={create} className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <h2 className="font-heading text-base font-semibold text-[#0F2645]">Intervention sur place</h2>
          <p className="mt-1 text-sm text-[#5C6A87] leading-relaxed">Le minimum pour démarrer : le nom du client. Vous complétez le reste sur le chantier.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="iv-new-client-name" required className={inputCls} placeholder="Nom du client *" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            <input data-testid="iv-new-client-phone" className={inputCls} placeholder="Téléphone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
            <input data-testid="iv-new-client-email" type="email" className={inputCls} placeholder="Email du client (pour envoyer le rapport)" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
            <input data-testid="iv-new-address" className={inputCls} placeholder="Adresse du chantier" value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} />
            <input data-testid="iv-new-postal" className={inputCls} placeholder="Code postal" value={form.site_postal_code} onChange={(e) => setForm({ ...form, site_postal_code: e.target.value })} />
            <input data-testid="iv-new-city" className={inputCls} placeholder="Ville" value={form.site_city} onChange={(e) => setForm({ ...form, site_city: e.target.value })} />
            <select data-testid="iv-new-trade" className={inputCls} value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value, job_type: (JOB_TYPES[e.target.value] || JOB_TYPES.autre)[0][0] })}>
              {TRADE_OPTIONS.map(([v, l]) => <option key={v} value={v} >{l}</option>)}
            </select>
            <select data-testid="iv-new-job-type" className={inputCls} value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })}>
              {jobTypes.map(([v, l]) => <option key={v} value={v} >{l}</option>)}
            </select>
          </div>
          <button type="submit" data-testid="iv-new-submit" disabled={saving} className={`${btnPrimary} mt-4`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Créer l'intervention
          </button>
        </form>
      )}

      <div data-testid="interventions-tabs" className="-mx-4 mt-6 flex gap-1 overflow-x-auto border-b border-[#E2E7F7] px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        {FILTERS.map(([v, l]) => {
          const active = filter === v;
          return (
            <button
              key={v}
              type="button"
              data-testid={`interventions-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1 text-sm font-semibold transition-colors duration-200 ${active ? "border-[#3A5CC8] text-[#3A5CC8]" : "border-transparent text-[#0F2645] hover:border-[#C7D2F5]"}`}
            >
              {l}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${active ? "bg-[#EEF1FC] text-[#3A5CC8]" : "bg-[#F3F5FC] text-[#5C6A87]"}`}>{counts[v] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#93A0BC]" />
        <input data-testid="interventions-search" className={`${inputCls} pl-9 bg-[#F6F8FD]`} placeholder="Rechercher une intervention…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <p data-testid="interventions-count" className="mt-3 text-sm text-[#5C6A87]">
        {visible.length} intervention{visible.length > 1 ? "s" : ""}
      </p>

      <div data-testid="interventions-list" className="mt-3 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
        {items === null && <ListSkeleton />}
        {visible.map((iv, i) => {
          const [label, badge, tint] = STATE_META[ivState(iv)];
          return (
            <div key={iv.id} style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }} className="dv-card-in group relative border-b border-[#EEF1FC] transition-colors last:border-b-0 hover:bg-[#F6F8FD]">
              <Link to={`/studio/interventions/${iv.id}`} data-testid={`intervention-row-${iv.reference}`} className="block px-4 py-3.5 pr-12 sm:px-5 sm:pr-14">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-heading text-sm font-extrabold text-[#0F2645]">
                    {iv.reference}
                    <span className="ml-2 text-xs font-semibold text-[#93A0BC]">{beDate(iv.date)}</span>
                  </p>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {iv.review?.rating && <span data-testid={`intervention-review-${iv.reference}`} className="inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {iv.review.rating}/5</span>}
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${badge}`}>{label}</span>
                  </span>
                </div>
                <p className="mt-1.5 flex min-w-0 items-center gap-2 text-sm text-[#0F2645]">
                  <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${tint}`}><UserRound className="h-3.5 w-3.5" strokeWidth={2.2} /></span>
                  <span className="truncate">
                    {iv.client_name}
                    <span className="text-[#93A0BC]">
                      {iv.site_city ? ` · ${iv.site_city}` : ""}{iv.dossier_reference ? ` · ${iv.dossier_reference}` : " · sans dossier"}
                    </span>
                  </span>
                </p>
              </Link>
              <button
                type="button"
                data-testid={`intervention-delete-${iv.reference}`}
                onClick={() => remove(iv)}
                aria-label="Supprimer l'intervention"
                className="absolute right-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#C5CDE3] transition-colors hover:bg-rose-50 hover:text-rose-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
        {items !== null && !visible.length && (
          <EmptyState
            testId="interventions-empty"
            icon={ClipboardCheck}
            title={items.length ? "Aucune intervention ne correspond" : "Aucune intervention pour l'instant"}
            hint={items.length ? "Modifiez la recherche ou le filtre." : "Créez-en une directement sur place, le rapport se signe au doigt."}
            action={!items.length && (
              <button type="button" data-testid="interventions-empty-new" onClick={() => setOpen(true)} className={`${btnPrimary} mt-2`}>
                <Plus className="h-4 w-4" /> Nouvelle intervention
              </button>
            )}
          />
        )}
      </div>
    </StudioLayout>
  );
}
